"use client";
import { useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { ShoppingCart, Heart, User, Menu, X, LogOut } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SignOutConfirmDialog } from "@/components/blocks/SignOutConfirmDialog";
import { ThemeToggle } from "@/features/theme/components/ThemeToggle";
import { HeaderProductSearch } from "@/features/public-site/components/HeaderProductSearch";
import { MobileSearchOverlay } from "@/features/public-site/components/MobileSearchOverlay";
import { useAuth } from "@/features/auth/contexts/AuthContext";
import { useCartTotalQuantity } from "@/features/shop/cart/cart.store";
import { getRoleHomePath } from "@/features/layout/role-paths";
import { cn } from "@/lib/utils";

interface PublicCommerceHeaderProps {
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  searchPlaceholder?: string;
}

const NAV_LINKS = [
  { label: "Home", href: "/" },
  { label: "Shop", href: "/shop" },
  { label: "Stores", href: "/shop/store-list" },
  { label: "Orders", href: "/shop/orders", customerOnly: true },
];

export function PublicCommerceHeader({
  searchValue,
  onSearchChange,
  searchPlaceholder = "Search products...",
}: PublicCommerceHeaderProps) {
  const pathname = usePathname();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { userAccount: user, loading, signOut } = useAuth();
  const isCustomer = !loading && user?.role === "customer";
  const cartItemCount = useCartTotalQuantity();
  const navLinks = NAV_LINKS.filter((item) => !item.customerOnly || isCustomer);

  const closeMobileMenu = () => setMobileMenuOpen(false);

  return (
    <header className="bg-background text-foreground shadow-sm">
      {/* Desktop & tablet top bar */}
      <div className="mx-auto flex max-w-7xl items-center gap-3 px-4 py-4 md:px-6">
        <Link
          href="/"
          className="flex items-center gap-2 font-black tracking-tight text-emerald-600"
        >
          <span className="relative h-10 w-10 overflow-hidden rounded-lg border border-border bg-card shadow-sm">
            <Image
              src="/logo-0.png"
              alt="Efasol logo"
              fill
              sizes="40px"
              className="object-cover"
              priority
            />
          </span>
          <span className="text-2xl">Efasol</span>
        </Link>

        {/* Desktop nav links */}
        <div className="hidden items-center gap-3 sm:ml-5 md:flex">
          {navLinks.map((item) => (
            <Link
              key={item.href}
              href={item.href}
              className={cn(
                "text-sm font-semibold transition-colors hover:text-emerald-600",
                pathname === item.href &&
                  "border-b-2 border-b-emerald-600 text-emerald-600",
              )}
            >
              {item.label}
            </Link>
          ))}
        </div>

        <div className="flex-1" />

        {/* Desktop search */}
        <div className="hidden lg:mx-3 lg:block">
          <HeaderProductSearch
            value={searchValue ?? ""}
            onChange={(next) => onSearchChange?.(next)}
            placeholder={searchPlaceholder}
            className="mx-auto w-[350px] max-w-xl"
            inputClassName="h-12 rounded-full border-border bg-muted pl-11 text-sm shadow-sm"
          />
        </div>

        {/* Desktop action buttons */}
        <div className="hidden items-center gap-2 md:flex">
          <ThemeToggle />
          <Button variant="brand" size="icon" className="rounded-full">
            <Heart className="h-4 w-4" />
          </Button>
          <Button
            asChild
            variant="brand"
            size="icon"
            className="relative rounded-full"
          >
            <Link href="/shop/cart" aria-label="Go to cart">
              <ShoppingCart className="h-4 w-4" />
              {cartItemCount > 0 && (
                <span className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-emerald-600 px-1 text-[10px] font-bold leading-none text-white">
                  {cartItemCount > 99 ? "99+" : cartItemCount}
                </span>
              )}
            </Link>
          </Button>
          {user ? (
            <>
              <Button
                asChild
                variant="brand"
                size="icon"
                className="rounded-full"
              >
                <Link
                  href={
                    user.role === "customer"
                      ? "/shop/profile"
                      : getRoleHomePath(user.role)
                  }
                >
                  <User className="h-4 w-4" />
                </Link>
              </Button>
              <SignOutConfirmDialog onConfirm={() => signOut()}>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label="Sign out"
                  className="rounded-full border border-border text-muted-foreground hover:border-destructive/40 hover:text-destructive"
                >
                  <LogOut className="h-4 w-4" />
                </Button>
              </SignOutConfirmDialog>
            </>
          ) : (
            <>
              <Button asChild variant="brand" className="rounded-md">
                <Link href="/login">Sign In</Link>
              </Button>
              <Button
                asChild
                className="rounded-md bg-emerald-600 text-white hover:bg-emerald-700"
              >
                <Link href="/register/customer">Sign Up</Link>
              </Button>
            </>
          )}
        </div>

        {/* Mobile right side: search, cart badge + hamburger */}
        <div className="flex items-center gap-2 md:hidden">
          <ThemeToggle />
          <MobileSearchOverlay />
          <Button
            asChild
            variant="brand"
            size="icon"
            className="relative rounded-full"
          >
            <Link href="/shop/cart" aria-label="Go to cart">
              <ShoppingCart className="h-4 w-4" />
              {cartItemCount > 0 && (
                <span className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-emerald-600 px-1 text-[10px] font-bold leading-none text-white">
                  {cartItemCount > 99 ? "99+" : cartItemCount}
                </span>
              )}
            </Link>
          </Button>
          <Button
            variant="ghost"
            size="icon"
            aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
            onClick={() => setMobileMenuOpen((prev) => !prev)}
            className="rounded-full border border-border text-foreground"
          >
            {mobileMenuOpen ? (
              <X className="h-5 w-5" />
            ) : (
              <Menu className="h-5 w-5" />
            )}
          </Button>
        </div>
      </div>

      {/* Mobile menu */}
      <div
        className={cn(
          "grid transition-[grid-template-rows] duration-300 ease-in-out md:hidden",
          mobileMenuOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
        )}
      >
        <div
          className={cn(
            "overflow-hidden transition-opacity duration-300 ease-in-out",
            mobileMenuOpen ? "opacity-100" : "opacity-0",
          )}
        >
          <div className="border-t border-border bg-background px-4 pb-6 pt-4">
            {/* Mobile nav links */}
            <nav className="mb-4 flex flex-col gap-1">
              {navLinks.map((item) => (
                <Link
                  key={item.href}
                  href={item.href}
                  onClick={closeMobileMenu}
                  className={cn(
                    "flex items-center rounded-lg px-3 py-2.5 text-sm font-semibold transition-colors hover:bg-primary/10 hover:text-primary",
                    pathname === item.href && "bg-primary/10 text-primary",
                  )}
                >
                  {item.label}
                </Link>
              ))}
            </nav>

            {/* Mobile auth actions */}
            <div className="flex flex-col gap-2 border-t border-border pt-4">
              {user ? (
                <>
                  <Button
                    asChild
                    variant="brand"
                    className="w-full justify-start gap-2 rounded-lg"
                  >
                    <Link
                      href={
                        user.role === "customer"
                          ? "/shop/profile"
                          : getRoleHomePath(user.role)
                      }
                      onClick={closeMobileMenu}
                    >
                      <User className="h-4 w-4" />
                      My Account
                    </Link>
                  </Button>
                  <SignOutConfirmDialog
                    onConfirm={() => {
                      closeMobileMenu();
                      signOut();
                    }}
                  >
                    <Button
                      variant="outline"
                      className="w-full justify-start gap-2 rounded-lg border-destructive/30 text-destructive hover:bg-destructive/10"
                    >
                      <LogOut className="h-4 w-4" />
                      Sign Out
                    </Button>
                  </SignOutConfirmDialog>
                </>
              ) : (
                <>
                  <Button asChild variant="brand" className="w-full rounded-lg">
                    <Link href="/login" onClick={closeMobileMenu}>
                      Sign In
                    </Link>
                  </Button>
                  <Button
                    asChild
                    className="w-full rounded-lg bg-emerald-600 text-white hover:bg-emerald-700"
                  >
                    <Link href="/register/customer" onClick={closeMobileMenu}>
                      Sign Up
                    </Link>
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}
